import { useState } from 'react';
import { Card, Button, Input, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useTrialBalance,
  useAccountingCtx,
  type TrialBalanceRow,
} from './useAccounting';

/* ═══════════════════════════════════════════════════════════════════════════
   Balancete de Verificação — por período (saldo inicial → movimento → final)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (rota real do backend — JournalController, sem prefixo):

   GET /trial-balance?date&dateFrom  →  TrialBalanceRowDto[] {
       accountCode, accountName, accountType, accountNature,
       totalDebit, totalCredit, netBalance,
       openingBalance,   // saldo inicial (antes de dateFrom); 0 sem dateFrom
       periodDebit,      // movimento de débitos em [dateFrom, date]
       periodCredit,     // movimento de créditos em [dateFrom, date]
       finalBalance      // saldo final (igual a netBalance)
     }

   NOTA DE ROTA: a tela do protótipo chama de "Balancete de Verificação" com
   colunas Account Code / Description / Initial Balance / Debits / Credits /
   Final Balance. A rota vive em /trial-balance (raiz, sem o prefixo
   /accounting) — é o mesmo endpoint que a aba Balancete de
   /accounting (FinancialStatementsPage) já consome via useTrialBalance.
   ═══════════════════════════════════════════════════════════════════════════ */

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  ASSET: 'Ativo',
  LIABILITY: 'Passivo',
  EQUITY: 'Patrimônio Líquido',
  REVENUE: 'Receita',
  EXPENSE: 'Despesa',
};

function todayIso(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function firstOfYearIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0] ?? '';
}

/* Valor negativo entre parênteses — convenção contábil. */
function formatAccounting(value: number): string {
  if (value < 0) return `(${formatCurrency(Math.abs(value))})`;
  return formatCurrency(value);
}

const TbStateLoading = ({ label }: { label: string }): JSX.Element => (
  <div className="rtb-state">
    <LoadingSkeleton />
    <span>{label}</span>
  </div>
);

const TbStateError = ({ label, error }: { label: string; error: unknown }): JSX.Element => (
  <div className="rtb-state rtb-state--error">
    <p className="rtb-state__title">{label}</p>
    <p className="rtb-state__detail">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
  </div>
);

const TbStateEmpty = ({ label }: { label: string }): JSX.Element => (
  <div className="rtb-state rtb-state--empty">
    <p>{label}</p>
  </div>
);

function TrialRow({ row }: { row: TrialBalanceRow }): JSX.Element {
  return (
    <tr className="rtb-row">
      <td className="rtb-mono">{row.code}</td>
      <td>{row.name}</td>
      <td className="rtb-muted">{ACCOUNT_TYPE_LABEL[row.type] ?? row.type}</td>
      <td className="rtb-center">
        <span className={row.nature === 'DEBIT' ? 'rtb-dc rtb-dc--d' : 'rtb-dc rtb-dc--c'}>
          {row.nature === 'DEBIT' ? 'D' : 'C'}
        </span>
      </td>
      <td className="rtb-num">{row.openingBalance !== 0 ? formatAccounting(row.openingBalance) : '—'}</td>
      <td className="rtb-num">{row.periodDebit > 0 ? formatCurrency(row.periodDebit) : '—'}</td>
      <td className="rtb-num">{row.periodCredit > 0 ? formatCurrency(row.periodCredit) : '—'}</td>
      <td className="rtb-num rtb-strong">{formatAccounting(row.finalBalance)}</td>
    </tr>
  );
}

export function TrialBalancePage(): JSX.Element {
  const [date, setDate] = useState(todayIso);
  const [dateFrom, setDateFrom] = useState(firstOfYearIso);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useTrialBalance(
    ctx,
    date || undefined,
    dateFrom || undefined,
  );

  const rows = data?.accounts ?? [];

  /* O balancete fecha quando o movimento do período equilibra: os débitos do
     período igualam os créditos do período. Os totais do período são somas das
     linhas exibidas (a listagem é completa — sem paginação). */
  const sumInitial = rows.reduce((s, r) => s + r.openingBalance, 0);
  const sumPeriodDebit = rows.reduce((s, r) => s + r.periodDebit, 0);
  const sumPeriodCredit = rows.reduce((s, r) => s + r.periodCredit, 0);
  const difference = +((sumPeriodDebit ?? 0) - (sumPeriodCredit ?? 0)).toFixed(2);

  return (
    <div className="rtb-page">
      <div className="mb-lg">
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Balancete de Verificação
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Saldo inicial, movimento do período (débitos e créditos) e saldo final por conta.
        </p>
      </div>

      <Card padding="1rem" variant="outlined" className="rtb-filters">
        <div className="rtb-filters__row">
          <div className="rtb-filters__field">
            <Input label="Data início" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="rtb-filters__field">
            <Input label="Data fim" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      <Card variant="outlined" padding="0">
        <div className="rtb-card__head">
          <h3 className="rtb-card__title">Balancete de Verificação</h3>
          <span className="rtb-card__period">
            {dateFrom ? `${formatDate(dateFrom)} a ${formatDate(date)}` : formatDate(date)}
          </span>
          <span className="rtb-card__count">
            {rows.length} {rows.length === 1 ? 'conta' : 'contas'}
          </span>
        </div>

        {isLoading && <TbStateLoading label="Carregando balancete..." />}
        {error && !isLoading && (
          <TbStateError label="Erro ao carregar balancete" error={error} />
        )}
        {!isLoading && !error && rows.length === 0 && (
          <TbStateEmpty label="Nenhuma conta com movimento até a data selecionada." />
        )}

        {!isLoading && !error && rows.length > 0 && (
          <div className="rtb-scroll">
            <table className="rtb-table">
              <thead>
                <tr>
                  <th>Account Code</th>
                  <th>Description</th>
                  <th>Tipo</th>
                  <th className="rtb-center">Nat.</th>
                  <th className="rtb-right">Initial Balance</th>
                  <th className="rtb-right">Debits</th>
                  <th className="rtb-right">Credits</th>
                  <th className="rtb-right">Final Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <TrialRow key={row.code} row={row} />
                ))}
              </tbody>
              <tfoot>
                <tr className="rtb-total">
                  <td colSpan={4}>Totais</td>
                  <td className="rtb-num">{formatAccounting(sumInitial)}</td>
                  <td className="rtb-num">{formatCurrency(sumPeriodDebit)}</td>
                  <td className="rtb-num">{formatCurrency(sumPeriodCredit)}</td>
                  <td className="rtb-num" />
                </tr>
                <tr className={difference === 0 ? 'rtb-check rtb-check--ok' : 'rtb-check rtb-check--bad'}>
                  <td colSpan={4}>Diferença</td>
                  <td className="rtb-num" colSpan={3}>{formatCurrency(difference)}</td>
                  <td className="rtb-center">
                    <span className="material-symbols-outlined rtb-check__icon" aria-hidden="true">
                      {difference === 0 ? 'check_circle' : 'error'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .rtb-page { padding: 1.5rem; }

        .rtb-filters { margin-bottom: 1rem; }
        .rtb-filters__row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .rtb-filters__field { flex: 0 0 180px; }

        .rtb-card__head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; padding: 1rem 1rem 0.75rem; border-bottom: 1px solid #eceef0; flex-wrap: wrap; }
        .rtb-card__title { margin: 0; font-size: 1.25rem; font-weight: 600; color: #191c1e; }
        .rtb-card__period { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
        .rtb-card__count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; white-space: nowrap; }

        .rtb-scroll { overflow-x: auto; }
        .rtb-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .rtb-table th { position: sticky; top: 0; text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .rtb-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; color: #191c1e; }

        .rtb-right { text-align: right; }
        .rtb-center { text-align: center; }
        .rtb-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .rtb-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .rtb-muted { color: #43474e; }
        .rtb-strong { font-weight: 600; }

        .rtb-row:hover td { background: #f2f4f6; }

        .rtb-dc { display: inline-block; min-width: 20px; padding: 0 4px; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; }
        .rtb-dc--d { background: #d6e3ff; color: #001b3c; }
        .rtb-dc--c { background: #e0e3e5; color: #191c1e; }

        .rtb-total td { background: #f7f9fb; font-weight: 600; border-top: 1px solid #c4c6cf; }
        .rtb-check td { font-weight: 600; }
        .rtb-check--ok td { color: #1b7f4d; }
        .rtb-check--bad td { color: #ba1a1a; }
        .rtb-check__icon { font-size: 16px; }

        .rtb-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 2rem; color: #43474e; font-size: 0.875rem; }
        .rtb-state--error { color: #ba1a1a; }
        .rtb-state--empty { color: #74777f; }
        .rtb-state__title { margin: 0; font-weight: 600; }
        .rtb-state__detail { margin: 0; font-size: 0.875rem; color: #74777f; }
      `}</style>
    </div>
  );
}